import { FC, KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isValid } from 'date-fns';
import { pickerLocale, toDate, VALUE_FORMAT } from './pickerLocale';
import './FilterDateField.scss';

export type FilterDateFieldProps = {
  label: string;
  /** `yyyy-MM-dd`, or empty for no date. */
  value: string;
  /** Called with `yyyy-MM-dd`, or `''` once the date is cleared. */
  onChange: (value: string) => void;
  /** Earliest day the calendar offers, `yyyy-MM-dd`. */
  min?: string;
  /** Latest day the calendar offers, `yyyy-MM-dd`. */
  max?: string;
};

/**
 * A filter date, taken from a calendar only. The browser's own date picker
 * moved the date along with the month as its arrows paged, and set the field
 * on every step; this one pages without touching the date, which changes only
 * when a day, Today or Clear is picked.
 *
 * The field cannot be typed into: a click, Enter or Space opens the calendar.
 */
export const FilterDateField: FC<FilterDateFieldProps> = ({
  label,
  value,
  onChange,
  min,
  max,
}) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  const { locale, localeText } = pickerLocale(i18n.language);

  const openOnKey = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen(true);
    }
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={locale}
      localeText={localeText}
    >
      <DatePicker
        className="filter-date-field"
        label={label}
        value={toDate(value)}
        onChange={(date) =>
          onChange(date && isValid(date) ? format(date, VALUE_FORMAT) : '')
        }
        minDate={toDate(min) ?? undefined}
        maxDate={toDate(max) ?? undefined}
        // An empty end opens on its start's month, not today's, and the other
        // way round: that is where the other half of the range is picked.
        referenceDate={toDate(min) ?? toDate(max) ?? undefined}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        enableAccessibleFieldDOMStructure={false}
        slotProps={{
          field: { readOnly: true },
          textField: {
            size: 'small',
            fullWidth: true,
            InputLabelProps: { shrink: true },
            onClick: () => setOpen(true),
            onKeyDown: openOnKey,
          },
          actionBar: {
            actions: ['clear', 'today'],
            className: 'filter-date-field__actions',
          },
          day: { className: 'filter-date-field__day' },
        }}
      />
    </LocalizationProvider>
  );
};
